import { useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { useAppStore } from "@/store";
import { usePermissions } from "@/hooks/use-permissions";
import { usePermits } from "@/hooks/use-permits";
import { useFacilities } from "@/hooks/use-facilities";
import { usePermitColumns } from "@/components/permits/permit-columns";
import { PermitFilters, type PermitFilterValues } from "@/components/permits/permit-filters";
import { type components } from "@njbsoft/types";
import {
  PageHeader,
  Button,
  DataTable,
  Pagination,
  EmptyState,
  SkeletonTable,
  Breadcrumbs,
} from "@/components/ui";

type PermitListItem = components["schemas"]["PermitListItem"];

interface PermitSearchParams {
  facility_id?: string;
}

export const Route = createFileRoute("/_authenticated/permits/")({
  component: PermitListPage,
  validateSearch: (search: Record<string, unknown>): PermitSearchParams => ({
    facility_id: typeof search.facility_id === "string" ? search.facility_id : undefined,
  }),
});

const PAGE_SIZE = 20;

function PermitListPage() {
  const { t } = useTranslation("permits");
  const navigate = useNavigate();

  const orgId = useAppStore((s) => s.activeOrgId);
  const { can } = usePermissions();
  const canCreate = can("permits", "create");
  const { facility_id: searchFacilityId } = Route.useSearch();

  const [filters, setFilters] = useState<PermitFilterValues>(() =>
    searchFacilityId ? { facility_id: searchFacilityId } : {},
  );
  const [offset, setOffset] = useState(0);
  const [sortBy, setSortBy] = useState<string | undefined>(undefined);
  const [sortDir, setSortDir] = useState<string | undefined>(undefined);

  const columns = usePermitColumns();

  const { data: facilitiesData } = useFacilities(orgId, {
    limit: 20,
    status: "active",
  });
  const facilities = facilitiesData?.data ?? [];

  const { data, isLoading, isError, error } = usePermits(orgId, {
    permit_type: filters.permit_type,
    status: filters.status,
    facility_id: filters.facility_id,
    search: filters.search,
    expiring_within_days: filters.expiring_within_days,
    limit: PAGE_SIZE,
    offset,
    sort_by: sortBy,
    sort_dir: sortDir,
  });

  const permits = data?.data ?? [];
  const total = data?.total ?? 0;

  const handleRowClick = (row: PermitListItem) => {
    void navigate({
      to: "/permits/$permitId",
      params: { permitId: row.id },
    });
  };

  const handleFilterChange = (newFilters: PermitFilterValues) => {
    setFilters(newFilters);
    setOffset(0);
  };

  const currentPage = Math.floor(offset / PAGE_SIZE) + 1;
  const totalPages = Math.ceil(total / PAGE_SIZE);

  if (!orgId) {
    return (
      <div className="p-8">
        <p className="text-muted">{t("noOrgContext", { ns: "org" })}</p>
      </div>
    );
  }

  return (
    <div className="p-8">
      <Breadcrumbs items={[{ label: t("title") }]} />
      <PageHeader
        title={t("title")}
        subtitle={t("subtitle")}
        actions={
          canCreate ? (
            <Link to="/permits/new">
              <Button>{t("addPermit")}</Button>
            </Link>
          ) : undefined
        }
      />

      <div className="mt-6">
        <PermitFilters
          filters={filters}
          onChange={handleFilterChange}
          facilities={facilities.map((f) => ({ id: f.id, name: f.name }))}
        />
      </div>

      {isError && (
        <div className="mt-6 rounded-md bg-danger/10 p-4 text-danger">
          {error instanceof Error ? error.message : t("unknownError", { ns: "common" })}
        </div>
      )}

      {isLoading ? (
        <div className="mt-6">
          <SkeletonTable columns={8} rows={5} />
        </div>
      ) : permits.length === 0 ? (
        <div className="mt-6">
          <EmptyState title={t("empty.title")} description={t("empty.description")} />
        </div>
      ) : (
        <>
          <div className="mt-6">
            <DataTable
              columns={columns}
              data={permits}
              onRowClick={handleRowClick}
              sorting={sortBy ? [{ id: sortBy, desc: sortDir === "desc" }] : []}
              onSortingChange={(next) => {
                const first = next[0];
                setSortBy(first?.id);
                setSortDir(first ? (first.desc ? "desc" : "asc") : undefined);
                setOffset(0);
              }}
            />
          </div>
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={total}
            pageSize={PAGE_SIZE}
            onPageChange={(page) => setOffset((page - 1) * PAGE_SIZE)}
          />
        </>
      )}
    </div>
  );
}
